import { GIFEncoder } from 'gifenc'
import {
  EXPORT_SIZE,
  GIF_FRAME_MS,
  PUNK_PIXELS,
  buildPalette,
  composite,
  glowOpacityAt,
  loadLayers,
  sameFrame
} from '@/punkImage'

// suiting up is purely visual and off-chain. this ports the original generator
// (radioactive-punks/generators/main-suits.js): the flattened punk sits behind the suit's visor,
// in a 3000x3000 space that all of the layers in public/suit share
export const SUIT_LAYOUT = {
  size: 3000,
  punk: { x: 1310, y: 684, size: 1100 }
}

// the suit outline's color, by the punk's lowercased Head trait. heads the original generator
// didn't know about (Red, Deep Blue, 100% Radioactive) get its default, as they always did
const HEAD_TO_GLOW = {
  white: 'green-1',
  black: 'green-1',
  rainbow: 'green-1',
  aquatic: 'blue',
  pink: 'pink',
  lizard: 'green-2',
  'glow in the dark': 'green-3',
  demon: 'red'
}
const DEFAULT_GLOW = 'green-1'

// a whole number of screen pixels per punk pixel: 22 here, 11 in the 720x720 gif
const PNG_SIZE = 1440

// walk sheets are 78px cells; the first four cells of the first four rows are each direction's loop
export const WALK_CELL = 78
const WALK_ROWS = { right: 0, up: 1, left: 2, down: 3 }
const WALK_FRAMES = 4
// keep in step with the suit-walk animation in SuitUpWindow.vue
const WALK_FRAME_MS = 150
const WALK_EXPORT_SIZE = WALK_CELL * 9

export function walkFrameOrigin (direction, frame) {
  return { x: frame * WALK_CELL, y: WALK_ROWS[direction] * WALK_CELL }
}

export function suitGlowFor (attributes) {
  const head = (attributes || []).find(attribute => attribute.trait_type === 'Head')
  return (head && HEAD_TO_GLOW[String(head.value).toLowerCase()]) || DEFAULT_GLOW
}

export function suitLayerUrl (name) {
  return `${process.env.BASE_URL}suit/${name}.png`
}

function loadImage (src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Could not load ${src}`))
    img.src = src
  })
}

function makeCanvas (size) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.imageSmoothingEnabled = false
  return { canvas, ctx }
}

// prepares what's common to every frame: the suit layers only need flattening once
async function prepareSuit (imageDataUrl, attributes, size) {
  const [punk, bg, suit, visor, glow] = await Promise.all([
    loadLayers(imageDataUrl),
    ...['bg', 'suit', 'visor-old', suitGlowFor(attributes)].map(name => loadImage(suitLayerUrl(name)))
  ])

  const overlay = makeCanvas(size)
  for (const layer of [suit, visor, glow]) {
    overlay.ctx.drawImage(layer, 0, 0, size, size)
  }

  const small = makeCanvas(PUNK_PIXELS)
  const { canvas, ctx } = makeCanvas(size)
  const scale = size / SUIT_LAYOUT.size
  const punkRect = [
    Math.round(SUIT_LAYOUT.punk.x * scale),
    Math.round(SUIT_LAYOUT.punk.y * scale),
    Math.round(SUIT_LAYOUT.punk.size * scale)
  ]

  const draw = punkPixels => {
    small.ctx.putImageData(new ImageData(punkPixels, PUNK_PIXELS, PUNK_PIXELS), 0, 0)
    ctx.drawImage(bg, 0, 0, size, size)
    ctx.drawImage(small.canvas, punkRect[0], punkRect[1], punkRect[2], punkRect[2])
    ctx.drawImage(overlay.canvas, 0, 0)
  }
  return { punk, canvas, ctx, draw }
}

// a still 1440x1440 png of the suited punk at full glow
export async function renderSuitedPNG (imageDataUrl, attributes) {
  const { punk, canvas, draw } = await prepareSuit(imageDataUrl, attributes, PNG_SIZE)
  draw(composite(punk, 1))
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}

// a 720x720 gif of exactly one glow cycle, which loops seamlessly forever
export async function renderSuitedGIF (imageDataUrl, attributes) {
  const { punk, ctx, draw } = await prepareSuit(imageDataUrl, attributes, EXPORT_SIZE)
  const loopMs = punk.animation.halfCycleMs * 2
  const frameCount = Math.round(loopMs / GIF_FRAME_MS)

  // merge runs of identical frames, comparing the cheap 24x24 punks rather than full frames
  const frames = []
  for (let i = 0; i < frameCount; i++) {
    const pixels = composite(punk, glowOpacityAt(i * GIF_FRAME_MS, punk.animation))
    const last = frames[frames.length - 1]
    if (last && sameFrame(last.pixels, pixels)) {
      last.delay += GIF_FRAME_MS
    } else {
      frames.push({ pixels, delay: GIF_FRAME_MS })
    }
  }

  const render = async frame => {
    draw(frame.pixels)
    // let the page breathe between frames
    await new Promise(resolve => setTimeout(resolve))
    return ctx.getImageData(0, 0, EXPORT_SIZE, EXPORT_SIZE).data
  }

  // these frames are far too big to keep them all around, so one pass collects the colors
  // and a second pass encodes
  const colors = new Map()
  for (const frame of frames) {
    const pixels = await render(frame)
    for (let i = 0; i < pixels.length; i += 4) {
      colors.set((pixels[i] << 16) | (pixels[i + 1] << 8) | pixels[i + 2], true)
    }
  }
  const swatch = new Uint8ClampedArray(colors.size * 4)
  let offset = 0
  for (const color of colors.keys()) {
    swatch.set([color >> 16, (color >> 8) & 255, color & 255, 255], offset)
    offset += 4
  }
  const { palette, index } = buildPalette([swatch])

  const gif = GIFEncoder()
  for (let i = 0; i < frames.length; i++) {
    gif.writeFrame(index(await render(frames[i])), EXPORT_SIZE, EXPORT_SIZE, {
      delay: frames[i].delay,
      ...(i === 0 ? { palette, repeat: 0 } : {})
    })
  }
  gif.finish()
  return new Blob([gif.bytes()], { type: 'image/gif' })
}

async function renderWalkFrames (sheetUrl, direction, frameCount) {
  const [sheet, bg] = await Promise.all([loadImage(sheetUrl), loadImage(suitLayerUrl('bg'))])
  const { canvas, ctx } = makeCanvas(WALK_EXPORT_SIZE)
  const frames = []
  for (let frame = 0; frame < frameCount; frame++) {
    const { x, y } = walkFrameOrigin(direction, frame)
    ctx.drawImage(bg, 0, 0, WALK_EXPORT_SIZE, WALK_EXPORT_SIZE)
    ctx.drawImage(sheet, x, y, WALK_CELL, WALK_CELL, 0, 0, WALK_EXPORT_SIZE, WALK_EXPORT_SIZE)
    frames.push(ctx.getImageData(0, 0, WALK_EXPORT_SIZE, WALK_EXPORT_SIZE).data)
  }
  return { canvas, frames }
}

// a still 702x702 png of the walk's first frame
export async function renderWalkPNG (sheetUrl, direction) {
  const { canvas } = await renderWalkFrames(sheetUrl, direction, 1)
  return new Promise(resolve => canvas.toBlob(resolve, 'image/png'))
}

// a 702x702 gif of one walk cycle, which loops seamlessly forever
export async function renderWalkGIF (sheetUrl, direction) {
  const { frames } = await renderWalkFrames(sheetUrl, direction, WALK_FRAMES)
  const { palette, index } = buildPalette(frames)
  const gif = GIFEncoder()
  frames.forEach((frame, i) => {
    gif.writeFrame(index(frame), WALK_EXPORT_SIZE, WALK_EXPORT_SIZE, {
      delay: WALK_FRAME_MS,
      ...(i === 0 ? { palette, repeat: 0 } : {})
    })
  })
  gif.finish()
  return new Blob([gif.bytes()], { type: 'image/gif' })
}
